import DeleteIcon from "@mui/icons-material/Delete";
import { Grid, Box, Button, IconButton } from "@mui/material";

import { EarData, FileMetadata } from "../../ProductForm.schema";

interface EarRequiredSectionProps {
  productForm: any;
  handleChange: (_newValue: EarData[]) => void;
}

const options = {
  attachmentType: [
    {
      value: "Entropy Assessment Report (EAR)",
      label: "Entropy Assessment Report (EAR)",
    },
    {
      value: "Supplemental EAR Documentation",
      label: "Supplemental EAR Documentation",
    },
  ],
  documentVisibility: [
    { value: "Public", label: "Public" },
    { value: "Proprietary", label: "Proprietary" },
  ],
};

const EarRequiredSection = ({
  productForm,
  handleChange,
}: EarRequiredSectionProps) => {
  const earData = productForm.state.values.ears.earData; // TODO: grab this data with tanstack

  const handleRemoveEar = (indexToRemove: number) => {
    if (earData.length === 1) {
      productForm.setFieldValue("ears.ear_required", false);
    }
    handleChange(
      earData.filter((_: EarData, index: number) => index !== indexToRemove),
    );
  };

  return (
    <>
      {earData.map((ear: EarData, index: number) => (
        <Box
          key={ear.earid}
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 1,
            boxShadow: 2,
            p: 2,
            mb: 3,
            position: "relative",
          }}
        >
          {(index > 0 || ear.earid) && (
            <IconButton
              onClick={() => handleRemoveEar(index)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "error.main",
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <productForm.AppField name={`ears.earData.${index}.apriori`}>
                {(field: any) => (
                  <field.SingleCheckbox label="This EAR was previously approved?" />
                )}
              </productForm.AppField>
            </Grid>
            <Grid item xs={12} sm={5}>
              <productForm.AppField name={`ears.earData.${index}.fileType`}>
                {(field: any) => (
                  <field.SelectField
                    options={options.attachmentType}
                    label="Document Type"
                    onChange={(val: string) => {
                      //@ts-ignore
                      productForm.setFieldValue(
                        `ears.earData.${index}.fileLabel`,
                        val,
                      );
                    }}
                  />
                )}
              </productForm.AppField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <productForm.AppField name={`ears.earData.${index}.fileLabel`}>
                {(field: any) => <field.TextInput label="File Label" />}
              </productForm.AppField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <productForm.Subscribe
                selector={(state: any) => ({
                  fileType: state.values.ears?.earData?.[index]?.fileType,
                  fileLabel: state.values.ears?.earData?.[index]?.fileLabel,
                })}
              >
                {({ fileType, fileLabel }: any) => {
                  const isFileUploadDisabled = !fileType || !fileLabel;

                  return (
                    <productForm.AppField
                      name={`ears.earData.${index}.earFiles`}
                    >
                      {(field: any) => (
                        <field.FileUpload
                          disabled={isFileUploadDisabled}
                          label="Choose File"
                          annotateFile={(file: File) => ({
                            fileType,
                            fileLabel,
                            fileName: file.name,
                            active: true,
                          })}
                        />
                      )}
                    </productForm.AppField>
                  );
                }}
              </productForm.Subscribe>
            </Grid>
            <Grid item xs={12}>
              <Box
                flex="1 1 100%"
                display="flex"
                gap={2}
                flexWrap="wrap"
                className="mt-3"
              >
                <productForm.AppField name={`ears.earData.${index}.earFiles`}>
                  {(field: any) =>
                    field.state.value
                      ?.filter((file: FileMetadata) => file.active)
                      .map((file: FileMetadata, fileIndex: number) => (
                        <field.FileCard
                          file={file.file || file}
                          title={file.fileLabel || file.fileType}
                          subheader={file.documentVisibility}
                          fileName={file.fileName}
                          index={fileIndex}
                          key={`${file.file_id || file.fileName}-${fileIndex}`}
                        />
                      ))
                  }
                </productForm.AppField>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ))}
      <Button
        variant="outlined"
        color="primary"
        fullWidth
        onClick={() => {
          handleChange([
            ...earData,
            {
              earid: undefined,
              apriori: false,
              earFiles: [],
            },
          ]);
        }}
      >
        + Add Another EAR
      </Button>
    </>
  );
};

export default EarRequiredSection;
